import { Controller } from "@hotwired/stimulus"

// Single application-wide activity feed controller
// Handles dynamic context switching for different models (Project, Card, Objective, etc)
export default class extends Controller {
  static targets = ["icon", "feed", "list", "form", "container"]
  static values = {
    contextType: String,  // e.g., "Project", "Card", "Objective"
    contextId: String      // e.g., "123"
  }

  connect() {
    // Check if page has activity context set via data attributes
    // Only load initial context if we don't already have one (prevents reset when moved to modal)
    this.loadInitialContext()
  }

  // Load context from page data attributes on connect
  loadInitialContext() {
    // Don't override existing context (important when activity feed is moved into modals)
    if (this.contextTypeValue && this.contextIdValue) {
      return
    }

    const contextType = document.body.dataset.activityContextType
    const contextId = document.body.dataset.activityContextId

    if (contextType && contextId) {
      this.setContext(contextType, contextId)
    }
  }

  // Update context and load activities for new context
  // Called by modal controllers or when context changes
  setContext(contextType, contextId) {
    this.contextTypeValue = contextType
    this.contextIdValue = contextId

    // Show the activity feed container
    this.containerTarget.classList.remove("hidden")

    // Load activities for this context
    this.loadContextContent()
  }

  // Clear context and hide activity feed
  clearContext() {
    this.contextTypeValue = ""
    this.contextIdValue = ""
    this.hide()
  }

  // Handle turbo:frame-load events from window
  // Check if the loaded content wants to clear the activity feed
  handleFrameLoad(event) {
    const frame = event.target
    if (frame.id === "content_modal_frame") {
      // Check if the loaded content has data-clear-activity-feed attribute
      const content = frame.querySelector('[data-clear-activity-feed]')
      if (content) {
        this.clearContext()
      }
    }
  }

  // Load activities and form for current context via Turbo Stream
  async loadContextContent() {
    if (!this.contextTypeValue || !this.contextIdValue) {
      return
    }

    try {
      const url = `/activities/load_context?context_type=${this.contextTypeValue}&context_id=${this.contextIdValue}`
      const response = await fetch(url, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html"
        }
      })

      if (response.ok) {
        const turboStream = await response.text()
        Turbo.renderStreamMessage(turboStream)
      }
    } catch (error) {
      console.error("Failed to load activity context:", error)
    }
  }

  expand() {
    // Hide icon, show feed panel
    this.iconTarget.classList.add("hidden")
    this.feedTarget.classList.remove("hidden")
  }

  collapse() {
    // Hide feed panel, show icon
    this.feedTarget.classList.add("hidden")
    this.iconTarget.classList.remove("hidden")
  }

  show() {
    // Show the entire activity feed container
    this.containerTarget.classList.remove("hidden")
  }

  hide() {
    // Hide the entire activity feed container (and collapse if expanded)
    if (!this.feedTarget.classList.contains("hidden")) {
      this.collapse()
    }
    this.containerTarget.classList.add("hidden")
  }
}
